import { useEffect, useState } from "react";
import { Inbox, FileText, Eye, CheckCircle2, XCircle, Ban, Loader2, Building2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TooltipButton } from "@/components/common/Tooltips";
import { api, apiError } from "@/lib/api";
import { fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ESTATUS_LABEL = {
  pendiente_aprobacion: "Pendiente",
  rechazada: "Rechazada",
  rechazado_definitivo: "Cerrada",
  Activa: "Aprobada",
};

export default function Solicitudes() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [todas, setTodas] = useState(false);
  const [sel, setSel] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = () => api.get(`/admin/solicitudes${todas ? "?todas=1" : ""}`)
    .then((r) => setRows(r.data)).catch(() => setDenied(true));

  useEffect(() => {
    if (user?.role !== "super_admin") { setDenied(true); return; }
    load();
  }, [user, todas]);

  const abrir = async (row) => {
    setSel(row); setDetalle(null); setMotivo("");
    try { const { data } = await api.get(`/admin/solicitudes/${row.id}`); setDetalle(data); }
    catch (e) { toast.error("No se pudo cargar la solicitud"); setSel(null); }
  };

  const verDoc = async (docId) => {
    try {
      const { data } = await api.post(`/onboarding/documentos/${docId}/signed-url`);
      window.open(`${BACKEND_URL}${data.url}`, "_blank", "noopener");
    } catch (e) { toast.error("No se pudo abrir el documento"); }
  };

  const aprobar = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/solicitudes/${sel.id}/aprobar`);
      toast.success("Entidad aprobada. El acceso del representante quedó habilitado.");
      setSel(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const rechazar = async (definitivo) => {
    if (!motivo.trim()) { toast.error("Indica el motivo del rechazo"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/solicitudes/${sel.id}/rechazar`, { motivo, definitivo });
      toast.success(definitivo ? "Solicitud cerrada definitivamente" : "Solicitud rechazada (el representante podrá corregir y reenviar)");
      setSel(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (denied)
    return (
      <div>
        <PageHeader title="Solicitudes de alta" subtitle="Panel de plataforma" />
        <div className="flex flex-col items-center justify-center gap-3 p-16 text-slate-400">
          <Inbox className="h-10 w-10" /><p className="text-sm">Acceso exclusivo del Super Administrador.</p>
        </div>
      </div>
    );

  const pendientes = rows.filter((r) => r.estatus === "pendiente_aprobacion").length;
  const d = detalle;

  return (
    <div>
      <PageHeader title="Solicitudes de alta de entidades" subtitle="Onboarding contractual B2B — revisa documentos corporativos y aprueba o rechaza" />
      <div className="space-y-5 p-6 md:p-8">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            <span data-testid="solicitudes-pendientes" className="font-semibold text-primary">{pendientes}</span> pendiente(s) de revisión
          </p>
          <Button data-testid="toggle-todas" variant="outline" size="sm" onClick={() => setTodas((v) => !v)}>
            {todas ? "Ver solo en proceso" : "Ver todas (incluye aprobadas)"}
          </Button>
        </div>

        <DataTable testid="table-solicitudes" rows={rows} onRowClick={abrir}
          columns={[
            { key: "nombre_comercial", label: "Entidad" },
            { key: "rfc", label: "RFC" },
            { key: "regimen", label: "Régimen", kind: "badge" },
            { key: "representante", label: "Representante" },
            { key: "documentos_count", label: "Docs" },
            { key: "estatus", label: "Estatus", render: (r) => <StatusBadge value={ESTATUS_LABEL[r.estatus] || r.estatus} /> },
            { key: "fecha_alta", label: "Fecha", kind: "date" },
          ]}
          actions={(r) => (
            <Button data-testid={`revisar-btn-${r.id}`} size="sm" variant="outline" onClick={() => abrir(r)}>Revisar</Button>
          )}
          emptyLabel="No hay solicitudes en proceso" />
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent data-testid="solicitud-dialog" className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <Building2 className="h-5 w-5 text-primary" /> {sel?.nombre_comercial || "Solicitud"}
            </DialogTitle>
          </DialogHeader>

          {!d ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm sm:grid-cols-3">
                {[
                  ["Razón social", d.razon_social], ["RFC", d.rfc], ["Régimen", d.regimen],
                  ["Representante", d.representante], ["Correo", d.representante_correo], ["Alta", fdate(d.fecha_alta)],
                ].map(([l, v]) => (
                  <div key={l}><p className="text-[11px] uppercase tracking-wide text-slate-500">{l}</p><p className="mt-0.5 font-medium text-slate-800">{v || "—"}</p></div>
                ))}
              </div>

              <div>
                <h4 className="mb-2 font-heading text-sm font-semibold text-slate-700">Documentos corporativos</h4>
                <div className="space-y-2">
                  {d.documentos.map((doc) => (
                    <div key={doc.tipo} className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-white px-3 py-2.5">
                      <div className="flex min-w-0 items-center gap-2.5">
                        {doc.subido ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> : <FileText className="h-4 w-4 shrink-0 text-slate-300" />}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-800">{doc.label}</p>
                          <p className="truncate text-xs text-slate-500">{doc.subido ? `${doc.nombre_archivo} · ${fdate(doc.fecha)}` : "No cargado"}</p>
                        </div>
                      </div>
                      {doc.subido && (
                        <Button data-testid={`sol-doc-view-${doc.tipo}`} variant="ghost" size="sm" onClick={() => verDoc(doc.doc_id)}>
                          <Eye className="mr-1 h-4 w-4" /> Ver
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {d.motivo_rechazo && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <span className="font-semibold">Último motivo de rechazo:</span> {d.motivo_rechazo}
                </div>
              )}

              {["pendiente_aprobacion", "rechazada"].includes(d.estatus) ? (
                <div className="space-y-3 border-t border-slate-100 pt-4">
                  <TooltipButton tooltip="Aprobar el alta de esta entidad (habilita el acceso del representante)"
                    data-testid="sol-aprobar" onClick={aprobar} disabled={busy} className="w-full bg-accent hover:bg-forest-700">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                    Aprobar y habilitar acceso
                  </TooltipButton>
                  <Textarea data-testid="sol-motivo" placeholder="Motivo del rechazo (requerido para rechazar)…" rows={2}
                    value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                  <div className="flex gap-2">
                    <TooltipButton tooltip="Rechazar la solicitud (permite corregir y reenviar)"
                      data-testid="sol-rechazar" variant="outline" disabled={busy}
                      className="flex-1 border-amber-200 text-amber-700 hover:bg-amber-50" onClick={() => rechazar(false)}>
                      <XCircle className="mr-1.5 h-4 w-4" /> Rechazar (permite reenvío)
                    </TooltipButton>
                    <TooltipButton tooltip="Cerrar definitivamente (la entidad no podrá volver a intentar)"
                      data-testid="sol-rechazar-definitivo" variant="outline" disabled={busy}
                      className="flex-1 border-red-200 text-red-600 hover:bg-red-50" onClick={() => rechazar(true)}>
                      <Ban className="mr-1.5 h-4 w-4" /> Rechazo definitivo
                    </TooltipButton>
                  </div>
                </div>
              ) : (
                <div className="rounded-md bg-slate-50 px-3 py-2 text-center text-sm text-slate-500">
                  Estatus actual: <StatusBadge value={ESTATUS_LABEL[d.estatus] || d.estatus} />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
